import { useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { MenuItem } from '@/types/index';
import { useCachedData } from './useCache';
import { CacheKeys, UserContext } from '@/stores/cacheKeys';
import { TTL_CONFIG } from '@/stores/cacheConfig';

export interface Category {
  id: string;
  branch_id: string;
  name: string;
  description: string | null;
  image_url: string | null;
  sort_order: number | null;
  is_active: boolean | null;
  created_at: string | null;
  updated_at: string | null;
  menu_items?: MenuItem[];
}

export const useCategories = (branchId?: string, options?: { enabled?: boolean }) => {
  const { user, isStaff, staffSession } = useAuth();
  const enabled = options?.enabled ?? true;

  // Create user context for caching (memoized to prevent dependency issues)
  const userContext: UserContext | null = useMemo(() => {
    return user || staffSession ? {
      userId: user?.id || staffSession?.id || '',
      restaurantId: staffSession?.restaurant_id || '',
      isStaff: isStaff,
      allowedBranches: staffSession?.allowed_branches || []
    } : null;
  }, [user, isStaff, staffSession]);

  // Generate cache key
  const cacheKey = userContext && branchId ? CacheKeys.categories(userContext, branchId) : '';

  // Define the fetcher function
  const fetchCategories = useCallback(async (): Promise<Category[]> => {
    // Check if user is authenticated (either owner or staff)
    if ((!user && !isStaff) || !branchId || !userContext) {
      return [];
    }

    // For staff users, check if they have access to the branch
    if (isStaff && staffSession) {
      if (staffSession.allowed_branches && staffSession.allowed_branches.length > 0) {
        if (!staffSession.allowed_branches.includes(branchId)) {
          throw new Error('Access denied to this branch');
        }
      }
    }

    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('branch_id', branchId)
        .order('sort_order', { ascending: true, nullsFirst: false });

      if (error) {
        throw error;
      }
      return data || [];
    } catch (err: unknown) {
      console.error("Error fetching categories: ", err);
      const newError = err as { message?: string };
      throw new Error(newError.message ?? 'An unknown error occurred');
    }
  }, [user, isStaff, staffSession, branchId, userContext]);

  // Use cached data hook
  const {
    data: categories,
    loading,
    error,
    isFromCache,
    refetch,
    invalidate: invalidateCachedData
  } = useCachedData<Category[]>(
    cacheKey,
    fetchCategories,
    {
      enabled: enabled && !!branchId && !!userContext,
      ttl: TTL_CONFIG.categories,
      fallbackToCache: true,
      backgroundRefresh: false
    }
  );

  // Legacy setter function for backward compatibility
  const setCategories = useCallback((newCategories: Category[]) => {
    // Note: This would need to update both cache and trigger a refetch
    // For now, we'll just invalidate the cache and let it refetch
    console.warn('setCategories called - this will invalidate cache and refetch');
    invalidateCachedData();
  }, [invalidateCachedData]);

  return { 
    categories: categories || [], 
    setCategories, 
    loading, 
    error, 
    isFromCache,
    refetch,
    invalidate: invalidateCachedData
  };
};
